import React, { useMemo, useState } from 'react';
import RichTextEditor from './RichTextEditor';
import VisualTableListEditor from './VisualTableListEditor';
import { removeTableAnchor } from './bookTableAnchor';

const apiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.error === 'string') return data.error;
  if (data && typeof data === 'object') {
    const firstValue = Object.values(data).flat().find(Boolean);
    if (typeof firstValue === 'string') return firstValue;
  }
  return error?.message || 'La création a échoué. Réessayez.';
};

const ContextualHierarchyCreatePanel = ({
  target,
  onUploadImage,
  onSubmit,
  onCancel,
}) => {
  const [draft, setDraft] = useState({
    title: '',
    content: '',
    images: [],
    tables: [],
  });
  const [tableValidity, setTableValidity] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const hasInvalidTable = useMemo(
    () =>
      draft.tables.some((_, tableIndex) => tableValidity[tableIndex] === false),
    [draft.tables, tableValidity]
  );

  const submit = async (event) => {
    event.preventDefault();
    const title = draft.title.trim();
    if (!title) {
      setError('Le titre est requis.');
      return;
    }
    if (hasInvalidTable) {
      setError('Corrigez les largeurs de colonnes avant d’enregistrer.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await onSubmit(target, { ...draft, title });
    } catch (submitError) {
      setError(apiErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      aria-label="Créer un élément hiérarchique"
      className="mt-3 grid min-w-0 gap-4 rounded-lg border border-blue-200 bg-blue-50 p-4"
      onSubmit={submit}
    >
      <label className="grid gap-1 text-sm font-medium text-gray-700">
        <span>Titre</span>
        <input
          type="text"
          aria-label="Titre du nouvel élément"
          value={draft.title}
          onChange={(event) => {
            setDraft((current) => ({ ...current, title: event.target.value }));
            if (error === 'Le titre est requis.') setError('');
          }}
          className="w-full rounded border border-gray-300 bg-white px-3 py-2"
        />
      </label>

      <div className="grid min-w-0 gap-1 text-sm font-medium text-gray-700">
        <span>Contenu</span>
        <RichTextEditor
          content={draft.content}
          images={draft.images}
          tables={draft.tables}
          onChange={(content) =>
            setDraft((current) => ({ ...current, content }))
          }
          onImagesChange={(images) =>
            setDraft((current) => ({ ...current, images }))
          }
          onTablesChange={(tables) =>
            setDraft((current) => ({ ...current, tables }))
          }
          onUploadImage={onUploadImage}
          showImageLibrary
          placeholder="Écrivez le contenu du nouvel élément ici..."
          ariaLabel="Contenu du nouvel élément"
          editable
        />
      </div>

      <VisualTableListEditor
        tables={draft.tables}
        onChange={(tables) =>
          setDraft((current) => ({ ...current, tables }))
        }
        onValidityChange={(tableIndex, isValid) =>
          setTableValidity((current) => ({
            ...current,
            [tableIndex]: isValid,
          }))
        }
        onDeleteTable={(tableId) =>
          setDraft((current) => ({
            ...current,
            content: removeTableAnchor(current.content, tableId),
          }))
        }
      />

      {hasInvalidTable && (
        <p className="text-sm text-red-700" role="alert">
          Corrigez les largeurs de colonnes avant d’enregistrer.
        </p>
      )}
      {error &&
        error !== 'Corrigez les largeurs de colonnes avant d’enregistrer.' && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={submitting || hasInvalidTable}
          className="rounded bg-green-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Création…' : 'Créer'}
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={onCancel}
          className="rounded bg-gray-200 px-3 py-2 text-sm text-gray-700 disabled:opacity-60"
        >
          Annuler
        </button>
      </div>
    </form>
  );
};

export default ContextualHierarchyCreatePanel;
